import { useMemo, useState } from 'react'
import {
  Box, Button, CloseButton, Drawer, Icon, Input, NativeSelect, Portal, Text, VStack,
} from '@chakra-ui/react'
import { FiChevronDown } from 'react-icons/fi'
import { useIsMobile } from '../../hooks/useIsMobile'

export interface SheetSelectOption { value: string; label: string; sublabel?: string }

interface SheetSelectProps {
  value?: string
  onChange: (value: string) => void
  options: SheetSelectOption[]
  placeholder?: string
  searchable?: boolean
  title?: string
  size?: 'sm' | 'md'
  'aria-label'?: string
}

// Responsive select: native <select> on desktop/tablet (OS-native open picker,
// styled closed box via the nativeSelect recipe); a searchable bottom sheet on
// phone. Controlled — drops into react-hook-form via Controller. Token-clean
// (no raw gray/blue/white literals) so it costs nothing on the style ratchet.
export function SheetSelect({
  value, onChange, options, placeholder = 'Select…', searchable, title, size, ...rest
}: SheetSelectProps) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ariaLabel = rest['aria-label']
  const selected = options.find((o) => o.value === value)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query])

  if (!isMobile) {
    return (
      <NativeSelect.Root size={size}>
        <NativeSelect.Field
          aria-label={ariaLabel}
          value={value ?? ''}
          onChange={(e) => onChange(e.currentTarget.value)}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    )
  }

  return (
    <>
      <Button
        aria-label={ariaLabel}
        variant="outline"
        size={size}
        w="full"
        justifyContent="space-between"
        fontWeight="400"
        onClick={() => setOpen(true)}
      >
        <Text truncate color={selected ? 'ink' : 'muted'}>{selected?.label ?? placeholder}</Text>
        <Icon asChild color="muted"><FiChevronDown /></Icon>
      </Button>

      <Drawer.Root open={open} placement="bottom" onOpenChange={(e) => { if (!e.open) { setOpen(false); setQuery('') } }}>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)" shadow="popover">
              <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
              <Drawer.Header borderBottomWidth="1px">{title ?? ariaLabel ?? placeholder}</Drawer.Header>
              <Drawer.Body>
                {searchable && (
                  <Input placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} mb={3} mt={2} />
                )}
                <VStack gap={1} align="stretch" pb={2}>
                  {filtered.map((o) => (
                    <Box
                      key={o.value}
                      asChild
                      textAlign="left"
                      minH="48px"
                      px={3}
                      py={2}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={o.value === value ? 'accent' : 'border'}
                      bg={o.value === value ? 'brand.subtle' : 'surface'}
                      _active={{ bg: 'canvas.2' }}
                    >
                      <button type="button" onClick={() => { onChange(o.value); setOpen(false); setQuery('') }}>
                        <Text fontSize="sm" color="ink">{o.label}</Text>
                        {o.sublabel && <Text fontSize="xs" color="muted">{o.sublabel}</Text>}
                      </button>
                    </Box>
                  ))}
                  {filtered.length === 0 && <Text fontSize="sm" color="muted">No matches</Text>}
                </VStack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  )
}
